import React, { useState } from 'react';
import './RefundMeetingModal.scss';
import Rodal from '../../../../../components/Rodal/Rodal';
import { ModalHeader, ModalBody, InputGroup, Input } from 'reactstrap';
import ButtonLoader from '../../../../../components/Layout/Buttons/ButtonLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarSign } from '@fortawesome/free-solid-svg-icons';

interface RefundMeetingModalProps {
    isVisible: boolean;
    onClose: () => void;
    pricePaid?: number | null;
    refundedAmount?: number | null;
    onRefund: (amount: number) => Promise<void>;
}

const RefundMeetingModal = (props: RefundMeetingModalProps) => {
    const [amount, setAmount] = useState<string>('');
    const [touched, setTouched] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const maxRefundable = (props.pricePaid ?? 0) - (props.refundedAmount ?? 0);
    const parsedAmount = parseFloat(amount);

    const getValidationError = (): string | null => {
        if (!touched) return null;
        if (amount === '' || isNaN(parsedAmount)) return 'Please enter an amount.';
        if (parsedAmount <= 0) return 'Amount must be greater than $0.';
        if (parsedAmount > maxRefundable)
            return `Amount cannot exceed $${maxRefundable.toFixed(2)}.`;
        return null;
    };

    const validationError = getValidationError();

    const handleRefund = async () => {
        setTouched(true);
        if (validationError !== null || amount === '' || isNaN(parsedAmount)) return;
        setIsLoading(true);
        try {
            await props.onRefund(parsedAmount);
            props.onClose();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="refund-meeting-modal-container">
            <Rodal
                visible={props.isVisible}
                onClose={props.onClose}
                animation="slideUp"
                className="refund-meeting-modal"
                showMask={true}
                width={400}
                showCloseButton={true}>
                <ModalHeader>
                    <b>Refund Meeting</b>
                </ModalHeader>
                <ModalBody>
                    <div className="refund-meeting-form">
                        <div className="refund-row">
                            <label>Amount Paid:</label>
                            <span>
                                {props.pricePaid != null
                                    ? `$${props.pricePaid.toFixed(2)}`
                                    : '—'}
                            </span>
                        </div>
                        <div className="refund-row">
                            <label>Already Refunded:</label>
                            <span>
                                {props.refundedAmount != null
                                    ? `$${props.refundedAmount.toFixed(2)}`
                                    : '—'}
                            </span>
                        </div>
                        <div className="refund-row refund-row-input">
                            <label>Refund Amount:</label>
                            <div className="refund-input-wrapper">
                                <InputGroup>
                                    <div className="input-group-prepend">
                                        <div className="input-group-text">
                                            <FontAwesomeIcon icon={faDollarSign} />
                                        </div>
                                    </div>
                                    <Input
                                        type="number"
                                        min={0}
                                        max={maxRefundable}
                                        step={0.01}
                                        value={amount}
                                        invalid={touched && validationError !== null}
                                        placeholder="0.00"
                                        onChange={(e) => {
                                            setTouched(true);
                                            setAmount(e.target.value);
                                        }}
                                        onBlur={() => setTouched(true)}
                                    />
                                </InputGroup>
                                {touched && validationError && (
                                    <div className="invalid-feedback d-block">
                                        {validationError}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="d-flex justify-content-end">
                            <button
                                className="btn btn-outline-secondary mr-2"
                                onClick={props.onClose}>
                                Cancel
                            </button>
                            <ButtonLoader
                                className="btn btn-primary"
                                buttonText="Refund"
                                loaderButtonText="Refund"
                                isLoading={isLoading}
                                disabled={false}
                                onClick={handleRefund}
                            />
                        </div>
                    </div>
                </ModalBody>
            </Rodal>
        </div>
    );
};

export default RefundMeetingModal;
